import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { noteStageLocked } from "../noc-stages/locked-store";
import { ApiError } from "./client";

/**
 * The retry policy IS the availability policy.
 *
 * Retry only what a retry can fix: a dependency that did not answer. Retrying
 * INVALID_CREDENTIALS hammers the login limiter on the user's behalf; retrying
 * NOT_FOUND is three round trips to learn the same thing; retrying
 * SESSION_REVOKED delays the sign-out the user needs to see.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
	if (!(error instanceof ApiError)) return false;
	return error.transient && failureCount < 3;
}

export const queryClient = new QueryClient({
	// M7c: a 403 STAGE_LOCKED from any call swaps the page for the "not open yet" screen (routes/_app.tsx).
	queryCache: new QueryCache({ onError: noteStageLocked }),
	mutationCache: new MutationCache({ onError: noteStageLocked }),
	defaultOptions: {
		queries: {
			retry: shouldRetry,
			// 1s, 2s, 4s — a restarting API container is usually back inside that.
			retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
			staleTime: 30_000,
			// The tab regaining focus is not a reason to re-fetch every list on
			// screen. WSL's dashboards stampeded the API every time an operator
			// alt-tabbed back from the print queue.
			refetchOnWindowFocus: false,
		},
		mutations: {
			// A write is not idempotent. Retrying one automatically is how you get
			// two print batches, and every retry is another audit_logs row.
			retry: false,
		},
	},
});
